// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import ReactDOM from 'react-dom';
import { useState, useRef, useCallback, useEffect } from 'react';
import { getImageSrc } from '../utils/imageUrl.ts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, ShoppingCart, Camera, X, Mail, CheckCircle2, XCircle,
  ChevronDown, ChevronRight, Download, Pencil, Trash2,
  ExternalLink, AlertCircle, ListChecks, PlusSquare, Copy, Check,
} from 'lucide-react';
import { api } from '../api/client.ts';
import DatePicker from '../components/DatePicker.tsx';
import { downloadExport } from '../utils/exportHelper.ts';
import { Order, Vendor } from '../types/index.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { spinIconOut } from '../utils/iconAnimations.ts';
import { useEscapeToClose } from '../hooks/useEscapeToClose.ts';
import { useOrderSuppliers } from '../hooks/useOrderSuppliers.ts';
import '../styles/buttonAnimations.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_EMAIL_SUBJECT = 'Order Approval Request - {item_name}';
const DEFAULT_EMAIL_BODY =
  `Hello,\n\nI am requesting approval for the following order:\n\nItem: {item_name}\nSupplier: {supplier}\nQuantity: {qty}\nUnit Price: ${'{unit_price}'}\nPO#: {po_number}\n\nPlease review and approve.\n\nThank you,\n{requester_name}`;

function fillTemplate(template: string, vars: Record<string, string>) {
  return Object.entries(vars).reduce((t, [k, v]) => t.split(`{${k}}`).join(v), template);
}

// ROOT-CAUSE FIX (Orders "Expected Delivery Date shows one day early" bug): expected_delivery_date
// is a plain DB DATE column -- a calendar date with no time-of-day or timezone meaning. It used to
// be rendered with `new Date(isoString).toLocaleDateString()`, which parses the API's timestamp
// and then formats it in the BROWSER's local timezone. For anyone west of UTC (Chris is Eastern),
// that reliably rolls a UTC-midnight-or-earlier timestamp back to the previous calendar day.
// Same pattern already fixed correctly in Reports.tsx's fmtDate -- reused here rather than
// reinvented: slice to the first 10 chars ('YYYY-MM-DD', tolerates either the new bare-date string
// server/src/db.ts now returns for DATE columns, or a legacy full ISO timestamp), then build the
// Date via the LOCAL three-argument constructor (new Date(y, m-1, d)) instead of parsing a string
// -- this interprets y/m/d as calendar-date components directly, with no UTC/local conversion step
// for anything to shift. CLAUDE_RULES.md's locked date convention: "new Date(y, m-1, d) local
// constructor -- never parseISO()".
function fmtExpectedDate(val: string | null | undefined): string | null {
  if (!val) return null;
  const s = String(val).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProgressPill({ order }: { order: Order }) {
  const n = (order.is_placed ? 1 : 0) + (order.is_ordered ? 1 : 0) + (order.is_delivered ? 1 : 0);
  // red -> orange -> green across the three real stages (Placed / +Ordered / +Delivered).
  const map = [
    { label: '0%',  bg: 'var(--bg-elevated)',          color: 'var(--text-muted)' },
    { label: '33%', bg: 'rgba(239,68,68,0.15)',         color: 'var(--accent-danger)' },
    { label: '66%', bg: 'rgba(245,158,11,0.15)',        color: 'var(--accent-warning)' },
    { label: '100%',bg: 'rgba(34,197,94,0.15)',         color: 'var(--accent-success)' },
  ];
  const m = map[n];
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
      background: m.bg, color: m.color,
    }}>{m.label}</span>
  );
}

function ImageCell({ imageUrl, name, onEnter, onLeave }: { imageUrl: string | null; name: string; onEnter: (e: React.MouseEvent) => void; onLeave: () => void }) {
  // Outer wrapper owns hover detection (stable bounds, no transform) so the jello-bounce
  // squish on the inner child can never shrink the hoverable box and kick the cursor
  // outside mid-animation — that was flickering the image preview near icon edges/corners.
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={onEnter}
      onMouseLeave={(e) => { onLeave(); spinIconOut(e); }}>
      <div className="combo-jello-spin" data-tint="neutral"
        style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 'var(--radius-sm)', padding: 4 }}>
        <Camera size={15} className="btn-icon" color={imageUrl ? 'var(--text-primary)' : 'var(--text-muted)'} style={{ cursor: 'default' }} />
      </div>
    </div>
  );
}

function ToggleBtn({ checked, enabled, onClick }: { checked: boolean; enabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={!enabled}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (enabled) onClick(); }}
      title={!enabled ? 'Complete previous step first' : (checked ? 'Click to uncheck' : 'Click to check')}
      style={{
        background: 'none', border: 'none', cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.3, padding: 4, display: 'flex', alignItems: 'center',
        transition: 'transform 0.1s',
      }}
      onMouseEnter={e => { if (enabled) (e.currentTarget as HTMLElement).style.transform = 'scale(1.2)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
    >
      {checked
        ? <CheckCircle2 size={20} color="#22c55e" />
        : <XCircle size={20} color="#ef4444" />}
    </button>
  );
}

function InlineText({ value, placeholder, onSave }: { value: string | null; placeholder?: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? '');
  if (editing) return (
    <input value={v} onChange={e => setV(e.target.value)}
      onBlur={() => { onSave(v); setEditing(false); }}
      onKeyDown={e => { if (e.key === 'Enter') { onSave(v); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
      autoFocus style={{ width: 90, padding: '1px 6px', fontSize: '0.8125rem', borderRadius: 4, border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-primary)' }} />
  );
  return (
    <span onClick={() => { setV(value ?? ''); setEditing(true); }}
      style={{ cursor: 'text', color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: value ? 'monospace' : 'inherit', fontSize: '0.8125rem' }}>
      {value ?? <em style={{ opacity: 0.5 }}>{placeholder ?? '—'}</em>}
    </span>
  );
}

function InlineQty({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(String(value));
  if (editing) return (
    <input type="number" min={1} value={v} onChange={e => setV(e.target.value)}
      onBlur={() => { onSave(Math.max(1, Number(v) || 1)); setEditing(false); }}
      onKeyDown={e => { if (e.key === 'Enter') { onSave(Math.max(1, Number(v) || 1)); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
      autoFocus style={{ width: 56, padding: '1px 4px', fontSize: '0.8125rem', borderRadius: 4, border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-primary)' }} />
  );
  return <span onClick={() => { setV(String(value)); setEditing(true); }} style={{ cursor: 'text' }}>{value}</span>;
}

// ─── Shared inline-DatePicker editing behaviour ────────────────────────────────────────────────
// Used by both InlinePlacedDate and InlineExpectedDate below -- previously each field invented its
// own interaction (InlinePlacedDate: blur-to-save on a native <input type="date">; InlineExpectedDate:
// an explicit Cancel-X button next to DatePicker, specifically to dodge the portal-popover-blur
// problem). Live-testing surfaced that having two DIFFERENT date-editing styles on the same page
// (a native segmented input for one field, DatePicker.tsx's own trigger for the other) read as "two
// different date pickers" -- not a bug inside DatePicker.tsx itself (it has exactly one render path,
// confirmed by re-reading it end to end), but two separate components, one of which (InlinePlacedDate)
// predates DatePicker.tsx's rollout and was never migrated. Standardizing both fields on DatePicker.tsx
// is what CLAUDE_RULES.md's own locked convention already calls for ("Replaces native <input
// type="date"> for new/converted work") -- this hook is what makes that consolidation behave
// identically in both places: click outside commits the current selection (same as the old
// InlinePlacedDate's blur-saves), Esc reverts without saving (a distinct, additional behavior from
// click-outside), and no separate Cancel button is needed for either.
//
// The outside-click check uses the `data-datepicker-popover` marker added to DatePicker.tsx's
// portal popover: a click is "outside" only when it lands neither inside this wrapper's own
// containerRef (which holds DatePicker's trigger button) NOR inside that portal popover -- without
// the second check, picking a day from the calendar (which lives in a document.body portal, not a
// DOM descendant of this wrapper) would read as an outside click and prematurely commit/close
// mid-selection.
function useInlineDateEditing(onCommit: (v: string) => void) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  function startEditing(current: string) {
    setV(current);
    setEditing(true);
  }

  function selectAndCommit(nv: string) {
    setV(nv);
    onCommit(nv);
    setEditing(false);
  }

  useEffect(() => {
    if (!editing) return;
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if ((target as HTMLElement).closest?.('[data-datepicker-popover]')) return;
      // Truly outside both the trigger and the calendar popover -- commit and close, same as a
      // native input's onBlur-saves behavior.
      onCommit(v);
      setEditing(false);
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCommit is a fresh closure every
    // render from the call site; re-subscribing on `v` alone (which DOES need to be current for
    // the handler to commit the right value) is what this needs, not a reference-identity re-run.
  }, [editing, v]);

  // Escape reverts WITHOUT saving -- deliberately does not call onCommit. `v` is simply discarded;
  // the next startEditing() call reinitializes it fresh from the current prop value, so there's
  // nothing stale to explicitly reset here.
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') setEditing(false);
  }

  return { editing, v, setV, wrapperRef, startEditing, selectAndCommit, handleKeyDown };
}

// orders.placed_date is a TIMESTAMPTZ (unlike expected_delivery_date, a real DATE column) -- it
// genuinely represents an instant (when this was marked Placed), not a pure calendar date, so the
// stored value stays a full ISO timestamp. Only the EDITING UI is standardized onto DatePicker.tsx
// here; what gets saved (date-at-local-midnight converted to an ISO instant) is unchanged from the
// native <input type="date"> this replaces.
function InlinePlacedDate({ value, onSave }: { value: string | null; onSave: (v: string) => void }) {
  const { editing, v, wrapperRef, startEditing, selectAndCommit, handleKeyDown } = useInlineDateEditing(
    (nv) => {
      // Empty (cleared) is ignored, matching the native <input type="date"> this replaces --
      // that one only ever called onSave from onBlur when a value was actually present.
      if (!nv) return;
      const [y, m, d] = nv.split('-').map(Number);
      onSave(new Date(y, m - 1, d).toISOString());
    }
  );
  const display = value ? new Date(value).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';
  if (editing) return (
    <div ref={wrapperRef} onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
      <DatePicker
        value={v}
        onChange={selectAndCommit}
        placeholder="Select date"
        // clearable={false}: clearing was never actually wired up for this field (the native
        // input it replaces only ever saved onBlur when a value was present, silently ignoring a
        // cleared/empty one) -- hiding the X/Clear affordance here instead of showing a control
        // that looks functional but no-ops.
        clearable={false}
        // fitContent: shrink to the date text + icon + chevron instead of stretching to the
        // table column's full rendered width (which is driven by the widest cell in the column,
        // not this one) -- that stretching was the large empty gap between the text and the
        // chevron reported from live-testing.
        fitContent
        style={{ padding: '2px 8px', fontSize: '0.8125rem', background: 'var(--bg-elevated)' }}
      />
    </div>
  );
  return (
    <span onClick={() => startEditing(value ? new Date(value).toISOString().slice(0, 10) : '')}
      style={{ cursor: 'text', fontSize: '0.8125rem', color: value ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
      {display}
    </span>
  );
}

// Inline Expected Delivery Date cell -- same click-to-edit UX as the PO# cell (InlineText above),
// via the shared DatePicker.tsx component and the shared editing behaviour above.
function InlineExpectedDate({ value, onSave }: { value: string | null | undefined; onSave: (v: string) => void }) {
  const { editing, v, wrapperRef, startEditing, selectAndCommit, handleKeyDown } = useInlineDateEditing(onSave);
  if (editing) return (
    <div ref={wrapperRef} onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
      <DatePicker
        value={v}
        onChange={selectAndCommit}
        placeholder="Select date"
        // See InlinePlacedDate's identical comment above -- same column-width-driven gap, same fix.
        fitContent
        style={{ padding: '2px 8px', fontSize: '0.8125rem', background: 'var(--bg-elevated)' }}
      />
    </div>
  );
  const display = fmtExpectedDate(value);
  return (
    <span onClick={() => startEditing(value ? value.slice(0, 10) : '')}
      style={{ cursor: 'text', fontSize: '0.7rem', color: display ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: display ? 600 : 400 }}>
      {/* Bright/prominent once set -- same "filled-in" treatment the PO# column (InlineText) uses
          for a filled value, visually distinct from the dim italic placeholder when unset. */}
      {display ? `Expected: ${display}` : <em style={{ opacity: 0.6 }}>+ Set expected date</em>}
    </span>
  );
}

// Overdue = has an expected delivery date in the past AND isn't already delivered. Uses the exact
// same timezone-safe local y/m/d comparison as fmtExpectedDate (the fix for the "Expected Delivery
// Date shows one day early" bug) -- NOT `new Date(dateStr) < new Date()`, which would silently
// reintroduce that same UTC-vs-local bug into a brand new comparison (an order due today could
// read as overdue a few hours early for anyone west of UTC).
function isOrderOverdue(order: Order): boolean {
  if (order.is_delivered) return false;
  const raw = (order as unknown as { expected_delivery_date?: string | null }).expected_delivery_date;
  if (!raw) return false;
  const s = String(raw).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return false;
  const due = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
}

// Copy-to-clipboard icon, PO# cell only -- small icon-only button (BUTTON_SYSTEM.md §3, Combo
// Jello Spin), data-tint="blue" matching the existing Share/export/link color mapping (copy is the
// same class of action). Only rendered when the cell actually has a value -- nothing to copy
// otherwise.
function CopyPoButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  async function handleCopy(e: React.MouseEvent) {
    e.stopPropagation(); // don't also trigger the InlineText edit-mode click right next to it
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard permission denied -- silently no-op, nothing destructive to roll back */ }
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      onMouseLeave={spinIconOut}
      className="combo-jello-spin" data-tint="blue"
      title={copied ? 'Copied!' : 'Copy PO#'}
      style={{ border: 'none', background: 'none', cursor: 'pointer', color: copied ? 'var(--accent-success)' : 'var(--text-muted)', padding: 2, display: 'flex', flexShrink: 0, borderRadius: 'var(--radius-sm)' }}
    >
      {copied ? <Check size={11} className="btn-icon" /> : <Copy size={11} className="btn-icon" />}
    </button>
  );
}

// ─── Email Modal ──────────────────────────────────────────────────────────────

const APPROVAL_EMAILS_KEY = 'proqure_approval_emails';

function loadSavedEmails(): string[] {
  try { return JSON.parse(localStorage.getItem(APPROVAL_EMAILS_KEY) || '[]'); } catch { return []; }
}

function saveEmail(email: string) {
  const saved = loadSavedEmails();
  if (!saved.includes(email)) {
    saved.unshift(email);
    localStorage.setItem(APPROVAL_EMAILS_KEY, JSON.stringify(saved.slice(0, 20)));
  }
}

function EmailModal({ order, poOrders, settings, requesterName, onClose, onSent }:
  { order: Order; poOrders: Order[]; settings: Record<string, string>; requesterName: string; onClose: () => void; onSent: () => void }) {
  const isMultiItem = poOrders.length > 1;

  const vars = {
    item_name: order.product_name,
    supplier: order.ordered_from,
    qty: String(order.quantity),
    unit_price: order.unit_price ? `$${parseFloat(String(order.unit_price || 0)).toFixed(2)}` : '—',
    po_number: order.po_number ?? '—',
    requester_name: requesterName,
  };

  function buildMultiItemBody(): string {
    const itemLines: string[] = [];
    let grandTotal = 0;
    poOrders.forEach((o, i) => {
      const price = parseFloat(String(o.unit_price || 0));
      const subtotal = price * o.quantity;
      grandTotal += subtotal;
      itemLines.push(`${i + 1}. ${o.product_name}`);
      itemLines.push(`   Qty: ${o.quantity} ${o.unit ?? 'ea'} | Unit Price: ${price > 0 ? `$${price.toFixed(2)}` : '—'} | Subtotal: ${price > 0 ? `$${subtotal.toFixed(2)}` : '—'}`);
      itemLines.push(`   Supplier: ${o.ordered_from}`);
    });
    return `Hello,\n\nI am requesting approval for PO# ${order.po_number} which includes the following items:\n\n${itemLines.join('\n')}\n\nGrand Total: $${grandTotal.toFixed(2)}\n\nPlease review and approve.\n\nThank you,\n${requesterName}`;
  }

  const [to, setTo] = useState(settings['order_email_recipient'] ?? '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(
    isMultiItem
      ? fillTemplate(settings['order_email_subject'] ?? DEFAULT_EMAIL_SUBJECT, { ...vars, item_name: `PO# ${order.po_number}` })
      : fillTemplate(settings['order_email_subject'] ?? DEFAULT_EMAIL_SUBJECT, vars)
  );
  const [body, setBody] = useState(() =>
    isMultiItem ? buildMultiItemBody() : fillTemplate(settings['order_email_body'] ?? DEFAULT_EMAIL_BODY, vars)
  );
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [toFocused, setToFocused] = useState(false);
  const [ccFocused, setCcFocused] = useState(false);
  const emailHistoryRef = useRef<string[]>(loadSavedEmails());
  const overlayRef = useRef<HTMLDivElement>(null);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);
  const qc = useQueryClient();

  const toSuggestions = toFocused && to.length > 0
    ? emailHistoryRef.current.filter(h => h.toLowerCase().includes(to.toLowerCase()) && h !== to)
    : [];

  const _ccParts = cc.split(',');
  const currentCCSegment = (_ccParts[_ccParts.length - 1] ?? '').trim();
  const ccSuggestions = ccFocused && currentCCSegment.length > 0
    ? emailHistoryRef.current.filter(h =>
        h.toLowerCase().includes(currentCCSegment.toLowerCase()) &&
        !cc.split(',').map(s => s.trim()).filter(Boolean).includes(h)
      )
    : [];

  function applyCCSuggestion(suggestion: string) {
    const parts = cc.split(',');
    parts[parts.length - 1] = ' ' + suggestion;
    setCc(parts.join(',') + ', ');
  }

  async function handleSend() {
    setSaving(true);
    setSendError(null);
    try {
      await api.post(`/orders/${order.id}/send-email`, { to, cc: cc.trim() || undefined, subject, body });
      // Save all addresses to shared localStorage history
      if (to.trim()) saveEmail(to.trim());
      cc.split(',').map((s: string) => s.trim()).filter(Boolean).forEach(saveEmail);
      emailHistoryRef.current = loadSavedEmails();
      setSent(true);
      await qc.invalidateQueries({ queryKey: ['orders'] });
      setTimeout(onSent, 1500);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSendError(msg ?? 'Failed to send email. Check SMTP settings.');
    } finally { setSaving(false); }
  }

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => { if (e.target === overlayRef.current) setMouseDownOnOverlay(true); }}
      onMouseUp={(e) => { if (mouseDownOnOverlay && e.target === overlayRef.current) onClose(); setMouseDownOnOverlay(false); }}
    >
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '90vw' }}>
        <div className="modal-header">
          <h3 className="modal-title"><Mail size={16} style={{ marginRight: 8 }} />Send Approval Email</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-group" style={{ position: 'relative' }}>
            <label className="form-label">To</label>
            <input
              className="form-input"
              type="email"
              value={to}
              onChange={e => setTo(e.target.value)}
              onFocus={() => setToFocused(true)}
              onBlur={() => setTimeout(() => setToFocused(false), 150)}
              placeholder="recipient@example.com"
            />
            {toSuggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: 160, overflowY: 'auto' }}>
                {toSuggestions.map(s => (
                  <div key={s}
                    onMouseDown={() => { setTo(s); setToFocused(false); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-primary)' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="form-group" style={{ position: 'relative' }}>
            <label className="form-label">CC</label>
            <input
              className="form-input"
              value={cc}
              onChange={e => setCc(e.target.value)}
              onFocus={() => setCcFocused(true)}
              onBlur={() => setTimeout(() => setCcFocused(false), 150)}
              placeholder="cc@example.com, another@example.com"
            />
            {ccSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                maxHeight: 160, overflowY: 'auto',
              }}>
                {ccSuggestions.map(s => (
                  <div key={s}
                    onMouseDown={() => applyCCSuggestion(s)}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-primary)' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Subject</label>
            <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          {isMultiItem && (
            <div className="form-group">
              <label className="form-label">Items included in this email ({poOrders.length})</label>
              <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                {poOrders.map((o, i) => {
                  const price = parseFloat(String(o.unit_price || 0));
                  const subtotal = price * o.quantity;
                  return (
                    <div key={o.id} style={{ padding: '8px 12px', borderBottom: i < poOrders.length - 1 ? '1px solid var(--border-subtle)' : undefined, fontSize: '0.8125rem' }}>
                      <div style={{ fontWeight: 500 }}>{i + 1}. {o.product_name}</div>
                      <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                        Qty: {o.quantity} {o.unit ?? 'ea'} &nbsp;|&nbsp; {price > 0 ? `$${price.toFixed(2)}/ea → $${subtotal.toFixed(2)}` : 'No price'} &nbsp;|&nbsp; {o.ordered_from}
                      </div>
                    </div>
                  );
                })}
                <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', fontSize: '0.8125rem', fontWeight: 600, textAlign: 'right' }}>
                  Grand Total: ${poOrders.reduce((s, o) => s + parseFloat(String(o.unit_price || 0)) * o.quantity, 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Body</label>
            <textarea className="form-textarea" rows={10} value={body} onChange={e => setBody(e.target.value)}
              style={{ minHeight: 200, fontFamily: 'monospace', fontSize: '0.8125rem' }} />
          </div>
          {sent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent-success)', fontSize: '0.875rem' }}>
              <CheckCircle2 size={15} /> Email sent successfully. Order marked as sent.
            </div>
          )}
          {sendError && (
            <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: 'var(--accent-danger)', fontSize: '0.8125rem' }}>
              ⚠ {sendError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSend} disabled={saving || !to}>
              <Mail size={15} /> {saving ? 'Sending…' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Add / Edit Item Modal ────────────────────────────────────────────────────

interface OrderForm {
  product_name: string; part_number: string; image_url: string; product_link: string;
  category: string; quantity: number; unit: string; unit_price: string;
  po_number: string; ordered_from: string; supplier_id: string; notes: string;
  inventory_item_id: string; expected_delivery_date: string;
}

function ItemModal({ initial, vendors, supplierList, onClose, onSave }:
  { initial?: Partial<OrderForm>; vendors: Vendor[]; supplierList: string[]; onClose: () => void; onSave: (f: OrderForm) => Promise<void> }) {
  // The Supplier select shows the "order_suppliers" facility-setting list (HD Supply, Grainger,
  // Amazon, DSSI… -- client/src/constants/suppliers.ts default), same list as Inventory's Add to
  // Order modal and Settings > Orders. This is NOT the vendors table (service companies) -- do
  // not confuse the two. `vendors` is kept only to resolve/preserve supplier_id, a soft link to
  // the vendors table the server LEFT JOINs for display (orders.ts ~50); it never drives what
  // the select shows or lets a user pick.
  const defaultOrderedFrom = initial ? (initial.ordered_from ?? '') : (supplierList[0] || 'DSSI');

  const [form, setForm] = useState<OrderForm>({
    product_name: '', part_number: '', image_url: '', product_link: '',
    category: '', quantity: 1, unit: 'ea', unit_price: '',
    po_number: '', supplier_id: initial?.supplier_id ?? '',
    notes: '', inventory_item_id: '', expected_delivery_date: '',
    ...initial,
    ordered_from: defaultOrderedFrom,
  });
  const [saving, setSaving] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  const sf = (k: keyof OrderForm, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.product_name || !form.ordered_from) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  }

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => { if (e.target === overlayRef.current) setMouseDownOnOverlay(true); }}
      onMouseUp={(e) => { if (mouseDownOnOverlay && e.target === overlayRef.current) onClose(); setMouseDownOnOverlay(false); }}
    >
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580, width: '90vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Fixed Header */}
        <div style={{ flexShrink: 0, padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="modal-header" style={{ marginBottom: 0 }}>
            <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingCart size={18} className="modal-icon-intro" style={{ color: '#3b82f6' }} />
              {initial ? 'Edit Order Item' : 'Add Order Item'}
            </h3>
            <button className="modal-close" onClick={onClose}><X size={18} className="btn-icon" /></button>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, padding: '20px 28px' }}>
          {/* Catalogue search bar removed -- it never worked (no functioning backing search/pick
              flow reachable from here) and wasn't needed; every field below is still editable
              directly. Supplier stays here (modal-only) -- it's deliberately NOT part of the new
              inline-editable cells (Orders.tsx's PO#/Expected Delivery Date), per the request. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Product Name *</label>
              <input className="form-input" required value={form.product_name} onChange={e => sf('product_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier *</label>
              <select className="form-select" value={form.ordered_from}
                onChange={e => {
                  const name = e.target.value;
                  // Keep supplier_id (the soft vendors-table link) in sync with the chosen
                  // supplier name when a vendor of that exact name exists, else clear it --
                  // never leave it pointing at the PREVIOUS supplier once the name has changed.
                  const matchedVendor = vendors.find(v => v.name.trim().toLowerCase() === name.trim().toLowerCase());
                  setForm(f => ({ ...f, ordered_from: name, supplier_id: matchedVendor ? String(matchedVendor.id) : '' }));
                }}>
                {supplierList.map(s => <option key={s} value={s}>{s}</option>)}
                {form.ordered_from && !supplierList.includes(form.ordered_from) && (
                  <option value={form.ordered_from}>{form.ordered_from}</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <input className="form-input" value={form.category} onChange={e => sf('category', e.target.value)} placeholder="e.g. Cleaning" />
            </div>
            <div className="form-group">
              <label className="form-label">Qty</label>
              <input className="form-input" type="number" min={1} value={form.quantity} onChange={e => sf('quantity', Number(e.target.value))} />
            </div>
            <div className="form-group">
              <label className="form-label">Unit</label>
              <select className="form-select" value={form.unit} onChange={e => sf('unit', e.target.value)}>
                {['ea','box','case','pack','pair','set','roll','bag','drum','gal'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Unit Price ($)</label>
              <input className="form-input" type="number" min={0} step="0.01" value={form.unit_price} onChange={e => sf('unit_price', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Part #</label>
              <input className="form-input" value={form.part_number} onChange={e => sf('part_number', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">PO#</label>
              <input className="form-input" value={form.po_number} onChange={e => sf('po_number', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Expected Delivery</label>
              <DatePicker value={form.expected_delivery_date} onChange={v => sf('expected_delivery_date', v)} placeholder="Select a date" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Product Link</label>
              <input className="form-input" type="url" value={form.product_link} onChange={e => sf('product_link', e.target.value)} placeholder="https://…" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => sf('notes', e.target.value)} />
            </div>
          </div>
        </div>
        {/* Fixed Footer */}
        <div style={{ flexShrink: 0, padding: '16px 28px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary combo2" disabled={saving}>
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Batch Edit Modal (2+ rows selected) ───────────────────────────────────────
// Replaces the single-item ItemModal when Edit is clicked with multiple rows selected. Scoped to
// only genuinely batch-editable fields -- Expected Delivery Date and PO# -- never Product Name/
// Supplier/Qty/Category/etc, which don't make sense to force-identical across different line
// items. Also absorbs the old standalone "Add PO#" toolbar button's one real job (bulk-assigning a
// PO# across multiple selected orders) -- the per-row inline "Add PO#" link already in the PO#
// column (InlineText) covers the single-item case, so a separate toolbar entry point for it was
// redundant once this modal exists.
//
// Each field has its own "Update ___" checkbox rather than inferring intent from whether the field
// was touched -- a batch operation should never silently apply just because a user glanced at a
// field, and leaving a field unchecked must be unambiguous "don't touch this field for anyone."
// Pinned header/footer + Esc-to-close (BUTTON_SYSTEM.md §9), same convention as the Bypass
// Subscription modal.
function BatchEditModal({ orders, onClose, onSave }: {
  orders: Order[];
  onClose: () => void;
  onSave: (opts: { expectedDeliveryDate?: string | null; poNumber?: string }) => Promise<void>;
}) {
  const [updateDate, setUpdateDate] = useState(false);
  const [dateVal, setDateVal] = useState('');
  const [updatePo, setUpdatePo] = useState(false);
  const [poVal, setPoVal] = useState('');
  const [saving, setSaving] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  useEscapeToClose(true, onClose);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const canSave = (updateDate) || (updatePo && poVal.trim().length > 0);

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        expectedDeliveryDate: updateDate ? (dateVal || null) : undefined,
        poNumber: updatePo ? poVal.trim() : undefined,
      });
    } finally { setSaving(false); }
  }

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => { if (e.target === overlayRef.current) setMouseDownOnOverlay(true); }}
      onMouseUp={(e) => { if (mouseDownOnOverlay && e.target === overlayRef.current) onClose(); setMouseDownOnOverlay(false); }}
    >
      <div className="modal modal-pinned" style={{ maxWidth: 440, width: '95vw', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
        <div className="modal-pinned-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <Pencil size={17} className="modal-icon-intro" style={{ color: '#3b82f6' }} />
            Edit {orders.length} Orders
          </h3>
          <button className="modal-close" onClick={onClose}><X size={18} className="btn-icon" /></button>
        </div>
        <div className="modal-pinned-body">
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 18 }}>
            Only fields checked below are applied, across all {orders.length} selected orders. Everything else (product, supplier, quantity, etc.) is left exactly as it is for each item.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                <input type="checkbox" checked={updateDate} onChange={e => setUpdateDate(e.target.checked)} />
                <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Update Expected Delivery Date</span>
              </label>
              {updateDate && (
                <DatePicker value={dateVal} onChange={setDateVal} placeholder="Select a date" />
              )}
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                <input type="checkbox" checked={updatePo} onChange={e => setUpdatePo(e.target.checked)} />
                <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Update PO#</span>
              </label>
              {updatePo && (
                <input className="form-input" value={poVal} onChange={e => setPoVal(e.target.value)} placeholder="e.g. PO-2025-001" autoFocus />
              )}
            </div>
          </div>
        </div>
        <div className="modal-pinned-footer">
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary combo2" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? 'Saving…' : `Save to ${orders.length} Order${orders.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Small numeric badge overlay for an icon-only toolbar button -- same established pattern as
// TopBar.tsx's chat-unread-count badge, scaled down to fit a compact toolbar button. Parent must
// be `position: relative`.
function CountBadge({ count, color }: { count: number; color: string }) {
  return (
    <div style={{
      position: 'absolute', top: -5, right: -5,
      background: color, color: '#fff',
      fontSize: 9, fontWeight: 700,
      borderRadius: 999, padding: '0 4px', minWidth: 15, height: 15,
      textAlign: 'center', lineHeight: '15px',
      border: '1.5px solid var(--bg-base)',
      pointerEvents: 'none',
    }}>
      {count > 99 ? '99+' : count}
    </div>
  );
}

// Icon-only toolbar action button -- combo-jello-spin + data-tint (BUTTON_SYSTEM.md §3), the same
// convention FacilityMap's own icon-only action buttons use. `title` carries the full label text
// the button used to show (tooltip, not removed -- just no longer rendered on the button face);
// `badge` reproduces whatever "(N)" count the button used to show inline, as a small corner badge
// (TopBar's unread-count pattern) rather than dropping that information when the text label goes
// away.
// Resting border/color per tint -- BUTTON_SYSTEM.md's data-tint color-reference table.
// combo-jello-spin's CSS only supplies the HOVER background for a given data-tint; the resting
// border/icon color is always the button's own inline style (documented explicitly in
// BUTTON_SYSTEM.md §3 and confirmed against FacilityMap's own icon-only action buttons, which set
// this the same way).
const TOOLBAR_TINT_STYLE: Record<string, { color: string; border: string }> = {
  neutral: { color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.18)' },
  green:   { color: '#34d399', border: '1px solid rgba(16,185,129,0.4)' },
  blue:    { color: '#60a5fa', border: '1px solid rgba(59,130,246,0.4)' },
  purple:  { color: '#a78bfa', border: '1px solid rgba(139,92,246,0.4)' },
  // Added for Delete -- see extraClassName below. Same color Combo 3 Danger (buttonAnimations.css)
  // itself uses (#f87171), set here INLINE instead of only via that class.
  red:     { color: '#f87171', border: '1px solid rgba(239,68,68,0.4)' },
};

// THE ACTUAL DIFF, found by comparing Delete's markup against this component (confirmed working)
// line by line, per the request: every OTHER toolbar button's resting color/border is set as an
// inline style here (`t.color`/`t.border`) -- inline style always wins regardless of any stylesheet
// load order or specificity. Delete, before this fix, was a separate hand-written <button> that set
// NEITHER color nor border inline -- it relied ENTIRELY on the `combo3-danger` CSS class
// (buttonAnimations.css) to supply both. That is a genuine, real structural difference from every
// button confirmed working, and the one place Delete and Export were NOT rendering through the same
// path. Rather than keep guessing at why the class might not be winning in one particular browser,
// Delete now renders through this exact same component (tint="red", extraClassName="combo3-danger"
// layers the shake-on-click + hover-tint mechanics on top) -- byte-for-byte the same structure as
// every other toolbar icon, nothing left for it to differ on.
function ToolbarIconButton({ icon, tint, title, onClick, disabled, badge, badgeColor, extraClassName }: {
  icon: React.ReactNode;
  tint: 'neutral' | 'green' | 'blue' | 'purple' | 'red';
  title: string;
  onClick: () => void;
  disabled?: boolean;
  badge?: number;
  badgeColor?: string;
  extraClassName?: string;
}) {
  const t = TOOLBAR_TINT_STYLE[tint];
  return (
    <button
      type="button"
      className={`btn btn-ghost btn-sm combo-jello-spin${extraClassName ? ` ${extraClassName}` : ''}`}
      data-tint={tint}
      onMouseLeave={spinIconOut}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, flexShrink: 0, padding: 0,
        color: disabled ? 'var(--text-muted)' : t.color,
        border: disabled ? '1px solid var(--border-subtle)' : t.border,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {icon}
      {badge !== undefined && badge > 0 && <CountBadge count={badge} color={badgeColor ?? 'var(--accent-primary)'} />}
    </button>
  );
}

// ─── Orders Page ──────────────────────────────────────────────────────────────

export default function OrdersPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [demoToast, setDemoToast] = useState<string | null>(null);
  const demoToastTimer = useRef<ReturnType<typeof setTimeout>>();
  function showDemoToast(msg = 'Demo mode — this action is view-only') {
    setDemoToast(msg);
    clearTimeout(demoToastTimer.current);
    demoToastTimer.current = setTimeout(() => setDemoToast(null), 3500);
  }
  function demoBlock(action: () => void) {
    if (isViewer) { showDemoToast(); return; }
    action();
  }
  const [tab, setTab] = useState<'active' | 'completed'>('active');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [showAddItem, setShowAddItem] = useState(false);
  const [editOrder, setEditOrder] = useState<Order | null>(null);
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [emailOrder, setEmailOrder] = useState<Order | null>(null);
  const [bulkEmailOrders, setBulkEmailOrders] = useState<Order[] | null>(null);
  const [completedMonth, setCompletedMonth] = useState(new Date().getMonth() + 1);
  const [completedYear, setCompletedYear] = useState(new Date().getFullYear());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmDeleteOverlayRef = useRef<HTMLDivElement>(null);
  const [confirmDeleteMouseDown, setConfirmDeleteMouseDown] = useState(false);
  useEscapeToClose(confirmDelete, () => setConfirmDelete(false));
  const [tooltip, setTooltip] = useState({ visible: false, itemId: null as number | null, x: 0, y: 0, imageUrl: '', itemName: '' });

  // ── URL-param highlight (global search navigation) ──
  const searchParams = new URLSearchParams(window.location.search);
  const highlightOrderId = searchParams.get('highlightOrder');
  const orderRowRefs = useRef<Record<number, HTMLElement | null>>({});
  const didHighlight = useRef(false);

  // ── Queries ──
  const ordersQ = useQuery({
    queryKey: ['orders', search],
    queryFn: () => api.get<Order[]>('/orders', search ? { search } : undefined),
  });
  const completedQ = useQuery({
    queryKey: ['orders-completed', completedMonth, completedYear],
    queryFn: () => api.get<Order[]>('/orders/completed', { month: completedMonth, year: completedYear }),
    enabled: tab === 'completed',
  });
  const vendorsQ = useQuery({
    queryKey: ['vendors-list'],
    queryFn: () => api.get<Vendor[]>('/vendors', { limit: 200 }),
  });
  // "Where we order from" list (facility setting order_suppliers) -- distinct from `vendors`
  // above. Same shared hook/cache key Inventory.tsx's Add to Order modal uses.
  const { supplierList } = useOrderSuppliers();
  const settingsQ = useQuery({
    queryKey: ['order-settings'],
    queryFn: () => api.get<Record<string, string>>('/settings/facility-settings', {
      keys: 'order_email_recipient,order_email_subject,order_email_body',
    }),
  });

  const activeOrders = (ordersQ.data as unknown as { data: Order[] })?.data ?? [];
  const completedOrders = (completedQ.data as unknown as { data: Order[] })?.data ?? [];
  const vendors = (vendorsQ.data as unknown as { data: Vendor[] })?.data ?? [];
  const emailSettings = (settingsQ.data as unknown as { data: Record<string, string> })?.data ?? {};

  // Scroll to and glow highlighted order from URL param
  useEffect(() => {
    if (!highlightOrderId || didHighlight.current || activeOrders.length === 0) return;
    const id = Number(highlightOrderId);
    const el = orderRowRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      didHighlight.current = true;
    }
  }, [activeOrders, highlightOrderId]);

  // ── Mutations ──
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Order> }) => api.put(`/orders/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/orders/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orders'] }); qc.invalidateQueries({ queryKey: ['orders-completed'] }); },
  });
  const createMut = useMutation({
    mutationFn: (data: Partial<Order>) => api.post<Order>('/orders', data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  const bulkPoMut = useMutation({
    mutationFn: ({ ids, po_number }: { ids: number[]; po_number: string }) =>
      api.post('/orders/bulk-po', { ids, po_number }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
  const moveCompletedMut = useMutation({
    mutationFn: () => api.post('/orders/move-completed', {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orders'] }); qc.invalidateQueries({ queryKey: ['orders-completed'] }); },
  });

  // ── Cross-page order refresh ──
  // When AddToOrderModal fires from Inventory or ItemDetail, invalidate here too
  useEffect(() => {
    const handler = (e: Event) => {
      const order = (e as CustomEvent).detail;
      if (order) {
        // Avoid duplicate if already present via invalidation
        qc.setQueryData(['orders', search], (old: unknown) => {
          const existing = (old as { data?: Order[] } | undefined)?.data ?? [];
          if (existing.find((o: Order) => o.id === order.id)) return old;
          return { ...(old as object), data: [order, ...existing] };
        });
      }
      qc.invalidateQueries({ queryKey: ['orders'] });
    };
    window.addEventListener('orderCreated', handler);
    return () => window.removeEventListener('orderCreated', handler);
  }, [qc, search]);

  // ── Helpers ──
  const deliveredCount = activeOrders.filter(o => o.is_delivered).length;

  function toggleGroup(poNum: string) {
    setCollapsedGroups(s => { const n = new Set(s); n.has(poNum) ? n.delete(poNum) : n.add(poNum); return n; });
  }
  function toggleSelect(id: number) {
    setSelectedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSelectedIds(s => s.size === activeOrders.length ? new Set() : new Set(activeOrders.map(o => o.id)));
  }

  const updateOrder = useCallback((id: number, patch: Partial<Order>) => {
    updateMut.mutate({ id, data: patch });
  }, [updateMut]);

  function handlePlacedToggle(order: Order) {
    if (order.is_placed) {
      updateOrder(order.id, { is_placed: false, placed_date: null, is_ordered: false, ordered_date: null, is_delivered: false, delivered_date: null });
    } else {
      updateOrder(order.id, { is_placed: true, placed_date: new Date().toISOString() });
    }
  }
  function handleOrderedToggle(order: Order) {
    if (!order.is_placed) return;
    if (order.is_ordered) {
      updateOrder(order.id, { is_ordered: false, ordered_date: null, is_delivered: false, delivered_date: null });
    } else {
      updateOrder(order.id, { is_ordered: true, ordered_date: new Date().toISOString() });
    }
  }
  function handleDeliveredToggle(order: Order) {
    if (!order.is_ordered) return;
    if (order.is_delivered) {
      updateOrder(order.id, { is_delivered: false, delivered_date: null });
    } else {
      updateOrder(order.id, { is_delivered: true, delivered_date: new Date().toISOString() });
    }
  }

  async function handleDeleteSelected() {
    for (const id of selectedIds) { await deleteMut.mutateAsync(id); }
    setSelectedIds(new Set());
    setConfirmDelete(false);
  }

  function exportCSV(completed: boolean) {
    const base = `/api/orders/export?completed=${completed}`;
    const extra = completed ? `&month=${completedMonth}&year=${completedYear}` : '';
    downloadExport(base + extra, 'orders-export.csv');
  }

  // ── Group active orders by PO# ──
  function buildRows(): React.ReactNode[] {
    const grouped = new Map<string, Order[]>();
    const ungrouped: Order[] = [];
    for (const o of activeOrders) {
      if (o.po_number) {
        const g = grouped.get(o.po_number) ?? [];
        g.push(o);
        grouped.set(o.po_number, g);
      } else {
        ungrouped.push(o);
      }
    }

    const rows: React.ReactNode[] = [];

    for (const [poNum, group] of grouped) {
      if (group.length > 1) {
        const collapsed = collapsedGroups.has(poNum);
        rows.push(
          <tr key={`group-header-${poNum}`} style={{ background: 'rgba(59,130,246,0.06)', borderLeft: '3px solid var(--accent-primary)' }}>
            <td colSpan={14}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', fontSize: '0.8125rem' }}>
                <button onClick={() => toggleGroup(poNum)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0, display: 'flex' }}>
                  {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </button>
                <span style={{ fontWeight: 600, color: 'var(--accent-primary)', fontFamily: 'monospace' }}>PO# {poNum}</span>
                <span style={{ color: 'var(--text-muted)' }}>— {group.length} items</span>
                <span style={{ color: 'var(--text-muted)' }}>— {group[0].ordered_from}</span>
              </div>
            </td>
          </tr>
        );
        if (!collapsed) {
          group.forEach(o => rows.push(renderRow(o, true)));
        }
      } else {
        rows.push(renderRow(group[0], false));
      }
    }

    ungrouped.forEach(o => rows.push(renderRow(o, false)));
    return rows;
  }

  function renderRow(order: Order, indented: boolean): React.ReactNode {
    const sel = selectedIds.has(order.id);
    const daysOld = Math.floor((Date.now() - new Date(order.created_at).getTime()) / 86400000);
    // Overdue takes visual priority over the age indicator below it (selected still wins over
    // both) -- a soft, status-only tint, not an alert. isOrderOverdue uses the same timezone-safe
    // local y/m/d comparison as fmtExpectedDate (the delivery-date display fix), not a raw
    // `new Date(x) < new Date()`, so this can't reintroduce that bug into a new comparison.
    const overdue = isOrderOverdue(order);
    const ageBg = !sel && daysOld >= 30 ? 'rgba(239,68,68,0.04)' : !sel && daysOld >= 15 ? 'rgba(245,158,11,0.04)' : undefined;
    const ageBorder = !indented && daysOld >= 30 ? '3px solid var(--accent-danger)' : !indented && daysOld >= 15 ? '3px solid var(--accent-warning)' : undefined;
    const rowBg = sel ? 'rgba(59,130,246,0.08)' : overdue ? 'rgba(239,68,68,0.055)' : ageBg;
    const rowBorder = indented ? '4px solid rgba(59,130,246,0.25)' : overdue ? '3px solid var(--accent-danger)' : ageBorder;
    return (
      <tr key={order.id}
        ref={(el) => { orderRowRefs.current[order.id] = el; }}
        className={order.id === Number(highlightOrderId) ? 'task-highlight-glow' : ''}
        title={overdue ? 'Expected delivery date has passed' : undefined}
        style={{
          background: rowBg,
          borderLeft: rowBorder,
        }}>
        <td style={{ width: 32, paddingLeft: indented ? 24 : undefined }}>
          <input type="checkbox" checked={sel} onChange={() => toggleSelect(order.id)} />
        </td>
        <td style={{ maxWidth: 180, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Per-row Edit -- moved to the front, next to Product Name. The toolbar's Edit action
                now operates on checkbox-selected rows (single or batch), so there was no way left
                to open the single-item "Edit Order Item" modal for one specific row without first
                selecting its checkbox. Opens it directly for THIS order, independent of selection
                state. */}
            <button onClick={() => demoBlock(() => setEditOrder(order))} onMouseLeave={spinIconOut}
              className="combo-jello-spin" data-tint="neutral"
              style={{ border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', flexShrink: 0 }}
              title="Edit this order">
              <Pencil size={13} className="btn-icon" />
            </button>
            <span title={order.product_name} style={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{order.product_name}</span>
            {daysOld >= 30 && <span style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 99, background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600, flexShrink: 0 }}>30d+</span>}
            {daysOld >= 15 && daysOld < 30 && <span style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 99, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600, flexShrink: 0 }}>15d+</span>}
          </div>
          {order.part_number && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>#{order.part_number}</div>}
        </td>
        <td style={{ textAlign: 'center' }}>
          {(() => {
            const rawUrl = (order as unknown as { effective_image_url?: string }).effective_image_url
              ?? order.image_url ?? order.thumbnail_path ?? null;
            const normalizedUrl = getImageSrc(rawUrl);
            return (
              <ImageCell
                imageUrl={normalizedUrl}
                name={order.product_name}
                onEnter={(e) => {
                  const PREVIEW_SIZE = 200;
                  const MARGIN = 12;
                  const left = e.clientX + MARGIN + PREVIEW_SIZE > window.innerWidth ? e.clientX - PREVIEW_SIZE - MARGIN : e.clientX + MARGIN;
                  const top = e.clientY + MARGIN + PREVIEW_SIZE > window.innerHeight ? e.clientY - PREVIEW_SIZE - MARGIN : e.clientY + MARGIN;
                  setTooltip({ visible: true, itemId: order.id, x: left, y: top, imageUrl: normalizedUrl ?? '', itemName: order.product_name });
                }}
                onLeave={() => setTooltip({ visible: false, itemId: null, x: 0, y: 0, imageUrl: '', itemName: '' })}
              />
            );
          })()}
        </td>
        <td style={{ textAlign: 'center' }}>
          {order.product_link
            ? <a href={order.product_link} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-primary)', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.75rem' }}>
                View <ExternalLink size={11} />
              </a>
            : null}
        </td>
        <td style={{ minWidth: 60 }}>
          <InlineQty value={order.quantity}
            onSave={v => updateOrder(order.id, { quantity: v, is_placed: order.is_placed, is_ordered: order.is_ordered, is_delivered: order.is_delivered, placed_date: order.placed_date, ordered_date: order.ordered_date, delivered_date: order.delivered_date })} />
        </td>
        <td style={{ textAlign: 'center' }}>
          <ToggleBtn checked={order.is_placed} enabled={true} onClick={() => handlePlacedToggle(order)} />
        </td>
        <td style={{ textAlign: 'center' }}>
          <ToggleBtn checked={order.is_ordered} enabled={order.is_placed} onClick={() => handleOrderedToggle(order)} />
        </td>
        <td style={{ textAlign: 'center' }}>
          <ToggleBtn checked={order.is_delivered} enabled={order.is_ordered} onClick={() => handleDeliveredToggle(order)} />
        </td>
        <td style={{ minWidth: 100 }}>
          <InlinePlacedDate value={order.placed_date}
            onSave={v => updateOrder(order.id, { placed_date: v, is_placed: order.is_placed, is_ordered: order.is_ordered, is_delivered: order.is_delivered, ordered_date: order.ordered_date, delivered_date: order.delivered_date })} />
        </td>
        <td><ProgressPill order={order} /></td>
        <td style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <button onClick={() => demoBlock(() => setEmailOrder(order))} onMouseLeave={spinIconOut}
              className="combo-jello-spin" data-tint="neutral"
              style={{ border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: order.email_sent ? 'var(--accent-success)' : 'var(--text-muted)', padding: 4, display: 'flex' }}
              title={order.email_sent ? `Sent ${order.email_sent_at ? new Date(order.email_sent_at).toLocaleDateString() : ''}` : 'Send email'}>
              <Mail size={15} className="btn-icon" />
            </button>
          </div>
        </td>
        <td
          title={order.ordered_from}
          // maxWidth/overflow/whiteSpace used to live on the <td> itself -- harmless while this
          // cell only ever showed static text, but it clipped InlineExpectedDate's DatePicker
          // trigger once editing needed more width than the collapsed display did (contributing
          // to the "looks cut off / garbled" symptom reported from live-testing). The supplier
          // name below already does its own overflow/ellipsis handling on its own div, so the
          // <td>-level constraint was redundant for display and actively harmful once editing.
          style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', maxWidth: 160 }}
        >
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.ordered_from}</div>
          {/* Inline-editable Expected Delivery Date -- same click-to-edit UX as the PO# cell
              (InlineText below), via the shared DatePicker.tsx component. */}
          <div style={{ marginTop: 1 }}>
            <InlineExpectedDate
              value={(order as unknown as { expected_delivery_date?: string | null }).expected_delivery_date}
              onSave={v => updateOrder(order.id, { expected_delivery_date: v || null, is_placed: order.is_placed, is_ordered: order.is_ordered, is_delivered: order.is_delivered, placed_date: order.placed_date, ordered_date: order.ordered_date, delivered_date: order.delivered_date } as Partial<Order>)}
            />
          </div>
        </td>
        <td>
          {order.category && (
            <span title={order.category} style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {order.category}
            </span>
          )}
        </td>
        <td style={{ minWidth: 90 }}>
          {/* Copy-to-clipboard -- PO# cell only (item 7), icon-only, not shown when empty. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <InlineText value={order.po_number} placeholder="Add PO#"
              onSave={v => updateOrder(order.id, { po_number: v || null, is_placed: order.is_placed, is_ordered: order.is_ordered, is_delivered: order.is_delivered, placed_date: order.placed_date, ordered_date: order.ordered_date, delivered_date: order.delivered_date } as Partial<Order>)} />
            {order.po_number && <CopyPoButton value={order.po_number} />}
          </div>
        </td>
      </tr>
    );
  }

  // ── Render ──

  const years = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div>
      {/* Demo-mode toast */}
      {demoToast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#b45309', color: '#fff', borderRadius: 8, padding: '10px 20px', fontSize: '0.875rem', fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={16} /> {demoToast}
        </div>
      )}
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">Orders</h2>
          <p className="page-subtitle">{tab === 'active' ? `${activeOrders.length} active orders` : `${completedOrders.length} completed`}</p>
        </div>
        {tab === 'active' && (
          <button className="btn combo5-hero" onClick={() => demoBlock(() => setShowAddItem(true))} onMouseLeave={spinIconOut}>
            <PlusSquare size={16} className="btn-icon" /> Add Item
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="pill-tab-container" style={{ marginBottom: 16 }}>
        <button className={`order-tab pill-tab${tab === 'active' ? ' active' : ''}`} onClick={() => setTab('active')}>
          <ListChecks size={14} className="btn-icon order-tab-icon" /> Active Orders
        </button>
        <button className={`order-tab pill-tab${tab === 'completed' ? ' active' : ''}`} onClick={() => setTab('completed')}>
          <CheckCircle2 size={14} className="btn-icon order-tab-icon" /> Completed
        </button>
      </div>

      {/* ── Active tab ── */}
      {tab === 'active' && (
        <>
          {/* Toolbar -- icon-only action buttons, wiggle-hover sourced from FacilityMap's own
              action buttons (combo-jello-spin + data-tint, BUTTON_SYSTEM.md §1/§3; the universal
              .btn-icon + spinIconOut wiggle-in/spin-out is already shared infra every icon button
              in this app gets, icon-only or not). Tooltips (title=) carry the full label text that
              used to be visible, including each button's existing count notifier -- nothing that
              was communicated before is gone, just moved off the button face. The old standalone
              "Add PO#" button is gone -- its one real job (bulk PO# across multiple selected
              orders) now lives in the batch-edit modal below (item 3/4). */}
          {/* ROOT CAUSE of Delete rendering as a clipped sliver: `marginLeft: 'auto'` inside a
              `flexWrap: 'wrap'` container has no guaranteed-visible floor -- without its own
              `flexShrink: 0`, the auto-margin computation (based on the FULL unwrapped width of
              everything ahead of it) could push Delete's wrapper past the toolbar's actual visible
              width before the browser's wrap decision caught up, and nothing stopped it shrinking
              to near-zero on top of that. Replaced with a plain two-group `justify-content:
              space-between` layout -- the standard, predictable way to pin one group flush right
              regardless of how the other group wraps -- and Delete's own group gets `flexShrink:
              0` so it is never the thing that gives when space is tight (the search bar and the
              other 4 icons shrink/wrap first). */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flex: '1 1 auto', minWidth: 0 }}>
              <div className="search-bar search-input-wrapper" style={{ flex: '1 1 200px', maxWidth: 320 }}>
                <Search size={14} color="var(--text-muted)" className="btn-icon" />
                <input placeholder="Search product, supplier, PO#…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>

              <ToolbarIconButton
                icon={<Download size={16} className="btn-icon" />}
                tint="blue"
                title="Export CSV"
                onClick={() => exportCSV(false)}
              />

              <ToolbarIconButton
                icon={<Pencil size={16} className="btn-icon" />}
                tint="neutral"
                title={selectedIds.size >= 2 ? `Batch Edit (${selectedIds.size} selected)` : 'Edit'}
                disabled={selectedIds.size === 0}
                onClick={() => demoBlock(() => {
                  if (selectedIds.size >= 2) { setShowBatchEdit(true); return; }
                  setEditOrder(activeOrders.find(o => selectedIds.has(o.id)) ?? null);
                })}
              />

              <ToolbarIconButton
                icon={<CheckCircle2 size={16} className="btn-icon" />}
                tint="green"
                title={`Move Completed${deliveredCount > 0 ? ` (${deliveredCount})` : ''}`}
                badge={deliveredCount > 0 ? deliveredCount : undefined}
                disabled={deliveredCount === 0}
                onClick={() => moveCompletedMut.mutate()}
              />

              <ToolbarIconButton
                icon={<Mail size={16} className="btn-icon" />}
                tint="purple"
                title={`Email Approval${selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ''}`}
                badge={selectedIds.size > 0 ? selectedIds.size : undefined}
                disabled={selectedIds.size === 0}
                onClick={() => demoBlock(() => {
                  const selected = activeOrders.filter(o => selectedIds.has(o.id));
                  if (selected.length === 0) return;
                  setBulkEmailOrders(selected);
                  setEmailOrder(selected[0]);
                })}
              />
            </div>

            {/* Delete -- its own group, pinned flush right by the outer space-between, with a
                visible divider, away from the other action buttons so it isn't easy to hit by
                accident. flexShrink: 0 on the group so it's always fully visible. Now rendered
                through the exact same ToolbarIconButton every other icon uses (tint="red" +
                extraClassName="combo3-danger" for the shake-on-click) instead of a separate
                hand-written <button> -- see ToolbarIconButton's own comment for the structural
                diff this replaced. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12, borderLeft: '1px solid var(--border-subtle)', flexShrink: 0 }}>
              <ToolbarIconButton
                icon={<Trash2 size={16} className="btn-icon" />}
                tint="red"
                extraClassName="combo3-danger"
                title={`Delete${selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ''}`}
                badge={selectedIds.size > 0 ? selectedIds.size : undefined}
                badgeColor="var(--accent-danger)"
                disabled={selectedIds.size === 0}
                onClick={() => demoBlock(() => setConfirmDelete(true))}
              />
            </div>
          </div>

          {ordersQ.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="loading-spinner" /></div>
          ) : activeOrders.length === 0 ? (
            <div className="empty-state">
              <ShoppingCart size={48} />
              <h3>No active orders</h3>
              <p>Add items from the Catalog or click "Add Item" above.</p>
              <button className="btn btn-primary" onClick={() => demoBlock(() => setShowAddItem(true))}><Plus size={16} /> Add Item</button>
            </div>
          ) : (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input type="checkbox"
                        checked={selectedIds.size === activeOrders.length && activeOrders.length > 0}
                        onChange={toggleAll} />
                    </th>
                    <th>Product Name</th>
                    <th style={{ textAlign: 'center' }}>Image</th>
                    <th style={{ textAlign: 'center' }}>Web</th>
                    <th>Qty</th>
                    <th style={{ textAlign: 'center' }}>Placed</th>
                    <th style={{ textAlign: 'center' }}>Ordered</th>
                    <th style={{ textAlign: 'center' }}>Delivered</th>
                    <th>Placed Date</th>
                    <th>Completed</th>
                    <th style={{ textAlign: 'center' }}>Email</th>
                    <th>Supplier</th>
                    <th>Category</th>
                    <th>PO#</th>
                  </tr>
                </thead>
                <tbody>{buildRows()}</tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Completed tab ── */}
      {tab === 'completed' && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <select className="form-select" style={{ width: 'auto' }} value={completedMonth} onChange={e => setCompletedMonth(Number(e.target.value))}>
              {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select className="form-select" style={{ width: 'auto' }} value={completedYear} onChange={e => setCompletedYear(Number(e.target.value))}>
              {years.map(y => <option key={y}>{y}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm combo1" onClick={() => exportCSV(true)} onMouseLeave={spinIconOut}>
              <Download size={14} className="btn-icon" /> Export
            </button>
            {selectedIds.size > 0 && (
              <button className="btn btn-ghost btn-sm combo3-danger" onClick={() => setConfirmDelete(true)} onMouseLeave={spinIconOut}>
                <Trash2 size={14} className="btn-icon" /> Delete ({selectedIds.size})
              </button>
            )}
          </div>

          {completedQ.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="loading-spinner" /></div>
          ) : completedOrders.length === 0 ? (
            <div className="empty-state">
              <CheckCircle2 size={48} />
              <h3>No completed orders for {months[completedMonth - 1]} {completedYear}</h3>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input type="checkbox"
                        checked={selectedIds.size === completedOrders.length}
                        onChange={() => setSelectedIds(s => s.size === completedOrders.length ? new Set() : new Set(completedOrders.map(o => o.id)))} />
                    </th>
                    <th>#</th>
                    <th style={{ textAlign: 'center' }}>Image</th>
                    <th>Product Name</th>
                    <th>Qty</th>
                    <th>Supplier</th>
                    <th>Category</th>
                    <th>Completed Date</th>
                    <th>PO Number</th>
                  </tr>
                </thead>
                <tbody>
                  {completedOrders.map((order, i) => (
                    <tr key={order.id} style={{ background: selectedIds.has(order.id) ? 'rgba(59,130,246,0.08)' : undefined }}>
                      <td><input type="checkbox" checked={selectedIds.has(order.id)} onChange={() => toggleSelect(order.id)} /></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{i + 1}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const rawUrl = (order as unknown as { effective_image_url?: string }).effective_image_url
                            ?? order.image_url ?? order.thumbnail_path ?? null;
                          const normalizedUrl = getImageSrc(rawUrl);
                          return (
                            <ImageCell
                              imageUrl={normalizedUrl}
                              name={order.product_name}
                              onEnter={(e) => {
                                const PREVIEW_HEIGHT = 200;
                                const PREVIEW_WIDTH = 200;
                                const MARGIN = 12;
                                const spaceBelow = window.innerHeight - e.clientY;
                                const showAbove = spaceBelow < PREVIEW_HEIGHT + MARGIN;
                                const spaceRight = window.innerWidth - e.clientX;
                                const left = spaceRight < PREVIEW_WIDTH + MARGIN ? e.clientX - PREVIEW_WIDTH - MARGIN : e.clientX + MARGIN;
                                const top = showAbove ? e.clientY - PREVIEW_HEIGHT - MARGIN : e.clientY + MARGIN;
                                setTooltip({ visible: true, itemId: order.id, x: left, y: top, imageUrl: normalizedUrl ?? '', itemName: order.product_name });
                              }}
                              onLeave={() => setTooltip({ visible: false, itemId: null, x: 0, y: 0, imageUrl: '', itemName: '' })}
                            />
                          );
                        })()}
                      </td>
                      <td style={{ fontWeight: 500 }}>{order.product_name}</td>
                      <td>{order.quantity}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{order.ordered_from}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{order.category ?? '—'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                        {order.completed_date ? new Date(order.completed_date).toLocaleDateString('en-US') : '—'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                        {order.po_number ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Modals ── */}

      {showAddItem && (
        <ItemModal
          vendors={vendors}
          supplierList={supplierList}
          onClose={() => setShowAddItem(false)}
          onSave={async (f) => {
            await createMut.mutateAsync({
              product_name: f.product_name, part_number: f.part_number || null,
              image_url: f.image_url || null, product_link: f.product_link || null,
              category: f.category || null, quantity: f.quantity,
              unit: f.unit, unit_price: f.unit_price ? Number(f.unit_price) : null,
              po_number: f.po_number || null, ordered_from: f.ordered_from,
              supplier_id: f.supplier_id ? Number(f.supplier_id) : null,
              notes: f.notes || null,
              inventory_item_id: f.inventory_item_id ? Number(f.inventory_item_id) : null,
            } as Partial<Order>);
            setShowAddItem(false);
          }}
        />
      )}

      {editOrder && (
        <ItemModal
          initial={{
            product_name: editOrder.product_name, part_number: editOrder.part_number ?? '',
            image_url: editOrder.image_url ?? '', product_link: editOrder.product_link ?? '',
            category: editOrder.category ?? '', quantity: editOrder.quantity,
            unit: editOrder.unit ?? 'ea', unit_price: editOrder.unit_price ? String(editOrder.unit_price) : '',
            po_number: editOrder.po_number ?? '', ordered_from: editOrder.ordered_from,
            supplier_id: editOrder.supplier_id ? String(editOrder.supplier_id) : '',
            notes: editOrder.notes ?? '',
            expected_delivery_date: (editOrder as unknown as { expected_delivery_date?: string }).expected_delivery_date ?? '',
          }}
          vendors={vendors}
          supplierList={supplierList}
          onClose={() => setEditOrder(null)}
          onSave={async (f) => {
            await updateMut.mutateAsync({
              id: editOrder.id,
              data: {
                product_name: f.product_name, part_number: f.part_number || null,
                image_url: f.image_url || null, product_link: f.product_link || null,
                category: f.category || null, quantity: f.quantity,
                unit: f.unit, unit_price: f.unit_price ? Number(f.unit_price) : null,
                po_number: f.po_number || null, ordered_from: f.ordered_from,
                supplier_id: f.supplier_id ? Number(f.supplier_id) : null,
                notes: f.notes || null,
                expected_delivery_date: f.expected_delivery_date || null,
                is_placed: editOrder.is_placed, is_ordered: editOrder.is_ordered,
                is_delivered: editOrder.is_delivered, placed_date: editOrder.placed_date,
                ordered_date: editOrder.ordered_date, delivered_date: editOrder.delivered_date,
              } as Partial<Order>,
            });
            setEditOrder(null);
            setSelectedIds(new Set());
          }}
        />
      )}

      {showBatchEdit && (
        <BatchEditModal
          orders={activeOrders.filter(o => selectedIds.has(o.id))}
          onClose={() => setShowBatchEdit(false)}
          onSave={async (opts) => {
            const ids = [...selectedIds];
            // PO#: reuse the existing bulk endpoint (one request for every selected order).
            if (opts.poNumber !== undefined) {
              await bulkPoMut.mutateAsync({ ids, po_number: opts.poNumber });
            }
            // Expected Delivery Date: no bulk endpoint for this field -- PUT /orders/:id already
            // does a server-side partial merge (COALESCE: provided value wins, else keep current),
            // confirmed by reading routes/orders.ts, so looping single-field updates is safe and
            // doesn't touch any other field on each order. Same loop-per-id shape already
            // established by handleDeleteSelected above.
            if (opts.expectedDeliveryDate !== undefined) {
              for (const id of ids) {
                await updateMut.mutateAsync({ id, data: { expected_delivery_date: opts.expectedDeliveryDate } as Partial<Order> });
              }
            }
            setShowBatchEdit(false);
            setSelectedIds(new Set());
          }}
        />
      )}

      {emailOrder && (
        <EmailModal
          order={emailOrder}
          poOrders={bulkEmailOrders ?? (emailOrder.po_number ? activeOrders.filter(o => o.po_number === emailOrder.po_number) : [emailOrder])}
          settings={emailSettings}
          requesterName="Facility Director"
          onClose={() => { setEmailOrder(null); setBulkEmailOrders(null); }}
          onSent={() => { setEmailOrder(null); setBulkEmailOrders(null); setSelectedIds(new Set()); }}
        />
      )}

      {/* Delete confirmation -- already existed (ad-hoc, no pinned header/footer or Esc-to-close);
          upgraded in place to BUTTON_SYSTEM.md §9's convention rather than rebuilt from scratch. */}
      {confirmDelete && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          ref={confirmDeleteOverlayRef}
          onMouseDown={(e) => { if (e.target === confirmDeleteOverlayRef.current) setConfirmDeleteMouseDown(true); }}
          onMouseUp={(e) => { if (confirmDeleteMouseDown && e.target === confirmDeleteOverlayRef.current) setConfirmDelete(false); setConfirmDeleteMouseDown(false); }}
        >
          <div className="modal modal-pinned" onClick={e => e.stopPropagation()} style={{ maxWidth: 380, width: '95vw' }}>
            <div className="modal-pinned-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="modal-title" style={{ margin: 0 }}><AlertCircle size={16} style={{ marginRight: 8, color: 'var(--accent-danger)' }} />Confirm Delete</h3>
              <button className="modal-close" onClick={() => setConfirmDelete(false)}><X size={18} className="btn-icon" /></button>
            </div>
            <div className="modal-pinned-body">
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                Delete {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.
              </p>
            </div>
            <div className="modal-pinned-footer">
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-danger combo3-danger" onClick={handleDeleteSelected}>Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image hover tooltip — fixed position, rendered at document root level */}
      {tooltip.visible && (
        <div style={{ position: 'fixed', left: tooltip.x, top: tooltip.y, zIndex: 9999, background: '#1f2937', borderRadius: 8, padding: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
          {tooltip.imageUrl ? (
            <img src={tooltip.imageUrl} style={{ width: 180, height: 180, objectFit: 'contain' }} alt={tooltip.itemName} />
          ) : (
            <div style={{ color: 'var(--text-secondary)', padding: 20 }}>No image</div>
          )}
          <div style={{ color: '#fff', fontSize: 12, marginTop: 8, maxWidth: 180 }}>{tooltip.itemName}</div>
        </div>
      )}
    </div>
  );
}
